import React, { useRef } from "react";
import { Typography, Button } from "@mui/material";
import { t } from "i18next";
import { PicassoResultsPayload, PicassoChallengeResult } from "../../shared/interfaces/GameLogicTypes";
import { sanitizeSvg, nextSvgUid } from "../SvgDrawing/SvgDrawing";
import "./PicassoResults.css";

interface PicassoResultsProps {
  results: PicassoResultsPayload;
  waitForHost: boolean;
  isHost: boolean;
  isReady: boolean;
  readyPlayerIds: string[];
  totalPlayers: number;
  onReadyToggle: (next: boolean) => void;
  onNextTurn: () => void;
}

function ChallengeCard({ challenge, uidA, uidB }: { challenge: PicassoChallengeResult; uidA: string; uidB: string }) {
  const subtitle =
    challenge.type === "imposter" && challenge.imposterName
      ? `vs. ${challenge.imposterName}'s imitation`
      : challenge.type === "archive"
      ? "vs. drawing from another lobby"
      : "vs. another player";

  // Compute per-drawer points for this challenge (exclude non-voters)
  const correctCount = challenge.voters.filter((v) => v.correct === true).length;
  const fooledCount = challenge.voters.filter((v) => v.correct === false).length;
  // Real drawer earns owner bonus (200 per correct guesser)
  const realDrawerPts = correctCount * 200;
  // Imposter/fake earns for fooling voters
  const fakeDrawerPts = challenge.type === "imposter" ? fooledCount * 200 : 0;

  const drawerAPts = challenge.realLabel === "A" ? realDrawerPts : fakeDrawerPts;
  const drawerBPts = challenge.realLabel === "B" ? realDrawerPts : fakeDrawerPts;
  const isArchiveA = challenge.type === "archive" && challenge.realLabel !== "A";
  const isArchiveB = challenge.type === "archive" && challenge.realLabel !== "B";

  return (
    <div className={`picasso-challenge-card ${challenge.type}`}>
      <div className="picasso-challenge-header">
        Which is {challenge.targetPlayerName}'s?
      </div>
      <div className="picasso-challenge-subtitle">{subtitle}</div>
      <div className="picasso-challenge-drawings">
        <div className="picasso-challenge-drawing-slot">
          <span className={`picasso-challenge-drawer-pts ${isArchiveA ? "empty" : ""}`}>
            {isArchiveA ? "\u00A0" : `+${drawerAPts}`}
          </span>
          <div
            className={`picasso-challenge-thumb ${challenge.realLabel === "A" ? "real-border" : ""}`}
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(challenge.drawingA, uidA) }}
          />
          <span className={`picasso-challenge-label ${challenge.realLabel === "A" ? "real" : ""}`}>
            {challenge.drawerAName} {challenge.realLabel === "A" ? "\u2713" : ""}
          </span>
        </div>
        <div className="picasso-challenge-drawing-slot">
          <span className={`picasso-challenge-drawer-pts ${isArchiveB ? "empty" : ""}`}>
            {isArchiveB ? "\u00A0" : `+${drawerBPts}`}
          </span>
          <div
            className={`picasso-challenge-thumb ${challenge.realLabel === "B" ? "real-border" : ""}`}
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(challenge.drawingB, uidB) }}
          />
          <span className={`picasso-challenge-label ${challenge.realLabel === "B" ? "real" : ""}`}>
            {challenge.drawerBName} {challenge.realLabel === "B" ? "\u2713" : ""}
          </span>
        </div>
      </div>
      <div className="picasso-challenge-voters">
        {challenge.voters.map((voter, idx) => {
          const icon = voter.correct === null ? "\u2014" : voter.correct ? "\u2713" : "\u2717";
          const cls = voter.correct === null ? "no-vote" : voter.correct ? "correct" : "wrong";
          return (
            <div key={idx} className={`picasso-challenge-voter ${cls}`}>
              <span className="picasso-challenge-voter-icon">{icon}</span>
              <span>{voter.playerName}</span>
              <span className="picasso-challenge-voter-pts">
                {voter.correct === null ? "no vote" : `+${voter.points}`}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GalleryDrawing({ drawing }: { drawing: PicassoResultsPayload["drawings"][number] }) {
  const uid = useRef(nextSvgUid()).current;
  return (
    <div className={`picasso-results-card ${drawing.isImposter ? "imposter" : ""}`}>
      <div
        className="drawing-thumb"
        dangerouslySetInnerHTML={{ __html: sanitizeSvg(drawing.drawingSvg, uid) }}
      />
      <span className="player-name">{drawing.playerName}</span>
      {drawing.isImposter && (
        <span className="imposter-badge">
          {t("picasso_impersonated")} {drawing.imposterTarget}
        </span>
      )}
    </div>
  );
}

function ChallengeCardWrapper({ challenge }: { challenge: PicassoChallengeResult }) {
  const uidA = useRef(nextSvgUid()).current;
  const uidB = useRef(nextSvgUid()).current;
  return <ChallengeCard challenge={challenge} uidA={uidA} uidB={uidB} />;
}

export default function PicassoResults({
  results,
  waitForHost,
  isHost,
  isReady,
  readyPlayerIds,
  totalPlayers,
  onReadyToggle,
  onNextTurn,
}: PicassoResultsProps) {
  return (
    <div className="picasso-results-container">
      <Typography variant="h5" color="white" fontWeight="bold">
        {t("picasso_gallery")}
      </Typography>

      {/* Drawings gallery — who drew what */}
      <div className="picasso-results-gallery">
        {results.drawings.map((drawing) => (
          <GalleryDrawing key={drawing.playerId} drawing={drawing} />
        ))}
      </div>

      {/* Challenge results — who voted on what and got it right/wrong */}
      {results.challengeResults && results.challengeResults.length > 0 && (
        <>
          <Typography variant="subtitle1" color="white" fontWeight="bold">
            {t("picasso_challenges")}
          </Typography>
          <div className="picasso-challenges-scroll">
            {results.challengeResults.map((challenge, i) => (
              <ChallengeCardWrapper key={i} challenge={challenge} />
            ))}
          </div>
        </>
      )}

      <div className="picasso-results-ready-controls">
        {waitForHost ? (
          isHost ? (
            <Button variant="contained" onClick={onNextTurn}>
              {t("continue")}
            </Button>
          ) : (
            <Typography color="grey.500" fontStyle="italic">
              {t("waiting_for_host")}
            </Typography>
          )
        ) : (
          <Button
            variant={isReady ? "outlined" : "contained"}
            onClick={() => onReadyToggle(!isReady)}
          >
            {isReady ? t("unready") : t("ready")} ({readyPlayerIds.length}/{totalPlayers})
          </Button>
        )}
      </div>
    </div>
  );
}
